<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">My Exit</h5>
        <p class="page-sub">Track your exit process and submission status</p>
      </div>
      <button
        v-if="!exitCase && !loading"
        class="btn-primary-action"
        @click="showResignModal = true"
      >
        <font-awesome-icon icon="file-signature" /> Submit Resignation
      </button>
    </div>

    <div v-if="loading" class="sk-table">
      <div v-for="n in 4" :key="n" class="sk-row"></div>
    </div>

    <!-- No active case -->
    <div v-else-if="!exitCase" class="empty-card">
      <font-awesome-icon icon="door-open" class="empty-icon" />
      <h3>No Active Exit Case</h3>
      <p>You don't have an active exit case. If you wish to resign, click the button above to submit your resignation.</p>
    </div>

    <!-- Active case -->
    <div v-else>
      <!-- Status strip -->
      <div class="status-strip" :class="'strip--' + exitCase.status">
        <div class="strip-left">
          <span class="strip-no">{{ exitCase.case_number }}</span>
          <span class="strip-type">{{ typeLabel(exitCase.type) }}</span>
        </div>
        <div class="strip-right">
          <span class="strip-status">{{ statusLabel(exitCase.status) }}</span>
          <button
            v-if="['draft','pending_approval'].includes(exitCase.status)"
            class="btn-withdraw"
            @click="showWithdrawConfirm = true"
          >
            Withdraw
          </button>
        </div>
      </div>

      <!-- Timeline steps -->
      <div class="steps-card">
        <div class="step-title">Progress</div>
        <div class="steps-row">
          <div v-for="step in steps" :key="step.key" class="step-item" :class="{ 'step--done': step.done, 'step--active': step.active }">
            <div class="step-circle">
              <font-awesome-icon v-if="step.done" icon="check" />
              <span v-else>{{ step.num }}</span>
            </div>
            <div class="step-label">{{ step.label }}</div>
          </div>
        </div>
      </div>

      <!-- Details card -->
      <div class="detail-card">
        <div class="detail-grid">
          <div class="d-block">
            <div class="d-label">Exit Date</div>
            <div class="d-val">{{ fmtDate(exitCase.exit_date) }}</div>
          </div>
          <div class="d-block">
            <div class="d-label">Last Working Day</div>
            <div class="d-val">{{ fmtDate(exitCase.last_working_day) }}</div>
          </div>
          <div class="d-block">
            <div class="d-label">Notice Period</div>
            <div class="d-val">{{ exitCase.notice_period_days ? exitCase.notice_period_days + ' days' : '—' }}</div>
          </div>
          <div class="d-block">
            <div class="d-label">Access Revoked</div>
            <div class="d-val">{{ exitCase.access_revoked_at ? fmtDate(exitCase.access_revoked_at) : 'Not yet' }}</div>
          </div>
        </div>
        <div v-if="exitCase.reason" class="reason-block">
          <div class="d-label">Your Reason</div>
          <div class="d-val">{{ exitCase.reason }}</div>
        </div>
      </div>

      <!-- Clearance summary -->
      <div v-if="exitCase.clearance_tasks?.length" class="section-card">
        <div class="section-card-title">Clearance ({{ clearanceDone }}/{{ exitCase.clearance_tasks.length }})</div>
        <div v-for="task in exitCase.clearance_tasks" :key="task.id" class="mini-task">
          <span class="mini-task-dept">{{ task.department }}</span>
          <span class="mini-task-name">{{ task.task_name }}</span>
          <span class="mini-task-st" :class="'st--' + task.status">{{ task.status }}</span>
        </div>
      </div>

      <!-- F&F summary -->
      <div v-if="exitCase.fnf_items?.length" class="section-card">
        <div class="section-card-title">F&F Settlement</div>
        <div v-for="item in exitCase.fnf_items" :key="item.id" class="mini-task">
          <span class="mini-task-name">{{ item.description }}</span>
          <span class="mini-task-st" :class="item.type === 'earning' ? 'st--completed' : 'st--pending'">{{ fmtAmt(item.amount) }}</span>
        </div>
      </div>
    </div>

    <!-- Resign modal -->
    <div v-if="showResignModal" class="dlg-overlay" @click.self="showResignModal=false">
      <div class="dlg-box">
        <div class="dlg-title">Submit Resignation</div>
        <div class="form-row" style="margin-bottom:12px;">
          <label class="form-label">Intended Last Working Day</label>
          <input v-model="resignForm.last_working_day" type="date" class="form-input" />
        </div>
        <div class="form-row" style="margin-bottom:12px;">
          <label class="form-label">Notice Period (days)</label>
          <input v-model.number="resignForm.notice_period_days" type="number" min="0" class="form-input" style="max-width:140px;" />
        </div>
        <div class="form-row" style="margin-bottom:16px;">
          <label class="form-label">Reason (optional)</label>
          <textarea v-model="resignForm.reason" class="form-input" rows="3" placeholder="Briefly describe your reason for leaving…" style="resize:vertical;"></textarea>
        </div>
        <div v-if="resignError" class="alert-error">{{ resignError }}</div>
        <div class="dlg-actions">
          <button class="btn-outline" @click="showResignModal=false">Cancel</button>
          <button class="btn-primary-action" @click="submitResignation" :disabled="submitting">
            <font-awesome-icon v-if="submitting" icon="spinner" spin />
            {{ submitting ? 'Submitting…' : 'Submit' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Withdraw confirm -->
    <div v-if="showWithdrawConfirm" class="dlg-overlay" @click.self="showWithdrawConfirm=false">
      <div class="dlg-box">
        <div class="dlg-title">Withdraw Resignation?</div>
        <p style="font-size:13px;color:#4a5e78;margin-bottom:20px;">This will cancel your resignation submission. You can re-submit at any time.</p>
        <div class="dlg-actions">
          <button class="btn-outline" @click="showWithdrawConfirm=false">No, keep it</button>
          <button class="btn-danger" @click="withdraw">Withdraw</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'MyExit',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true,
      exitCase: null,
      showResignModal: false,
      showWithdrawConfirm: false,
      submitting: false,
      resignError: '',
      resignForm: { last_working_day: '', notice_period_days: 30, reason: '' },
    }
  },
  computed: {
    clearanceDone() {
      return (this.exitCase?.clearance_tasks ?? []).filter(t => ['completed','waived'].includes(t.status)).length
    },
    steps() {
      if (!this.exitCase) return []
      const s = this.exitCase.status
      const order = ['pending_approval', 'approved', 'in_progress', 'clearance', 'fnf_pending', 'completed']
      const idx = order.indexOf(s)
      return [
        { num: 1, key: 'pending_approval', label: 'Submitted',  done: idx >= 0, active: idx === 0 },
        { num: 2, key: 'approved',         label: 'Approved',   done: idx >= 1, active: idx === 1 },
        { num: 3, key: 'clearance',        label: 'Clearance',  done: idx >= 3, active: idx === 3 },
        { num: 4, key: 'fnf_pending',      label: 'F&F',        done: idx >= 4, active: idx === 4 },
        { num: 5, key: 'completed',        label: 'Done',       done: idx >= 5, active: idx === 5 },
      ]
    },
  },
  created() { this.loadCase() },
  methods: {
    async loadCase() {
      this.loading = true
      try {
        const r = await axios.get('exits/me')
        this.exitCase = r.data.data
      } catch { /* no active case */ }
      finally { this.loading = false }
    },
    async submitResignation() {
      this.resignError = ''
      this.submitting = true
      try {
        const r = await axios.post('exits/me/resign', {
          last_working_day:   this.resignForm.last_working_day || undefined,
          notice_period_days: this.resignForm.notice_period_days || undefined,
          reason:             this.resignForm.reason || undefined,
        })
        this.exitCase = r.data.data
        this.showResignModal = false
        this.toast.success('Resignation submitted for approval.')
      } catch (e) {
        this.resignError = e.response?.data?.message ?? 'Submission failed.'
      } finally {
        this.submitting = false
      }
    },
    async withdraw() {
      try {
        await axios.post(`exits/me/${this.exitCase.id}/withdraw`)
        this.exitCase = null
        this.showWithdrawConfirm = false
        this.toast.success('Resignation withdrawn.')
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to withdraw.')
      }
    },
    fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—' },
    fmtAmt(v)  { return Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    typeLabel(t) {
      const m = { resignation:'Resignation', termination:'Termination', retirement:'Retirement', redundancy:'Redundancy', death:'Death' }
      return m[t] || t
    },
    statusLabel(s) {
      const m = { draft:'Draft', pending_approval:'Pending Approval', approved:'Approved', in_progress:'In Progress',
                  clearance:'Clearance', fnf_pending:'F&F Pending', completed:'Completed', cancelled:'Cancelled' }
      return m[s] || s
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#004AAD; font-size:13px; font-weight:600; padding:0 16px; height:36px; border-radius:8px; border:1.5px solid #004AAD; cursor:pointer; font-family:inherit; }
.btn-outline:hover { background:#f0f4ff; }
.btn-danger { background:#ef4444; color:#fff; font-size:13px; font-weight:600; padding:0 16px; height:36px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-withdraw { background:#fff; color:#ef4444; font-size:12px; font-weight:600; padding:0 12px; height:30px; border-radius:6px; border:1.5px solid #ef4444; cursor:pointer; font-family:inherit; }

.empty-card { text-align:center; background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:48px 24px; }
.empty-icon { font-size:40px; color:#dde5f0; margin-bottom:12px; display:block; }
.empty-card h3 { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.empty-card p  { font-size:13px; color:#7a90b0; margin:0; }

.status-strip { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-radius:12px; margin-bottom:14px; gap:12px; flex-wrap:wrap; }
.strip--pending_approval { background:#fef3c7; }
.strip--approved         { background:#dcfce7; }
.strip--in_progress      { background:#dbeafe; }
.strip--clearance        { background:#fce7f3; }
.strip--fnf_pending      { background:#fee2e2; }
.strip--completed        { background:#dcfce7; }
.strip--cancelled        { background:#f1f5f9; }
.strip--draft            { background:#f1f5f9; }
.strip-left  { display:flex; align-items:center; gap:10px; }
.strip-no    { font-size:14px; font-weight:700; color:#1a2b45; }
.strip-type  { font-size:12px; font-weight:600; color:#4a5e78; }
.strip-right { display:flex; align-items:center; gap:10px; }
.strip-status { font-size:12px; font-weight:700; color:#1a2b45; }

.steps-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:20px; margin-bottom:14px; }
.step-title { font-size:12px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; margin-bottom:16px; }
.steps-row  { display:flex; gap:0; overflow-x:auto; }
.step-item  { flex:1; min-width:70px; display:flex; flex-direction:column; align-items:center; text-align:center; position:relative; }
.step-item::before { content:''; position:absolute; top:14px; left:50%; right:-50%; height:2px; background:#e4eaf2; z-index:0; }
.step-item:last-child::before { display:none; }
.step-circle { width:28px; height:28px; border-radius:50%; background:#e4eaf2; color:#7a90b0; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; position:relative; z-index:1; flex-shrink:0; }
.step--done .step-circle  { background:#004AAD; color:#fff; }
.step--active .step-circle { background:#004AAD; color:#fff; box-shadow:0 0 0 4px rgba(0,74,173,.2); }
.step-label { font-size:10px; font-weight:600; color:#7a90b0; margin-top:6px; }
.step--done .step-label, .step--active .step-label { color:#004AAD; }

.detail-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:20px; margin-bottom:14px; }
.detail-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:14px; margin-bottom:16px; }
.d-block  { }
.d-label  { font-size:11px; font-weight:700; color:#7a90b0; text-transform:uppercase; margin-bottom:4px; }
.d-val    { font-size:13px; font-weight:600; color:#1a2b45; }
.reason-block { border-top:1px solid #f0f4f8; padding-top:14px; }

.section-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:16px 20px; margin-bottom:14px; }
.section-card-title { font-size:12px; font-weight:700; color:#7a90b0; text-transform:uppercase; margin-bottom:12px; }
.mini-task  { display:flex; align-items:center; gap:8px; padding:7px 0; border-bottom:1px solid #f0f4f8; }
.mini-task:last-child { border-bottom:none; }
.mini-task-dept { font-size:10px; font-weight:700; color:#7a90b0; flex:0 0 70px; }
.mini-task-name { font-size:12px; color:#1a2b45; flex:1; }
.mini-task-st   { font-size:10px; font-weight:700; padding:2px 7px; border-radius:5px; }
.st--pending   { background:#fef3c7; color:#92400e; }
.st--completed { background:#dcfce7; color:#166534; }
.st--waived    { background:#f1f5f9; color:#6b7280; }

.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.alert-error { background:#fee2e2; color:#991b1b; border-radius:8px; padding:10px 14px; font-size:13px; margin-top:10px; }

.dlg-overlay { position:fixed; inset:0; background:rgba(0,0,0,.4); display:flex; align-items:center; justify-content:center; z-index:1050; padding:16px; }
.dlg-box { background:#fff; border-radius:14px; padding:24px; width:100%; max-width:440px; }
.dlg-title { font-size:16px; font-weight:700; color:#1a2b45; margin-bottom:16px; }
.dlg-actions { display:flex; gap:10px; justify-content:flex-end; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:56px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
